import React, { useState, useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Card, CardContent, CardHeader, Grid, Button } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import Alert from '@material-ui/lab/Alert';
import Snackbar from '@material-ui/core/Snackbar';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import SertisseurEnvoiForm from '../form/SertisseurEnvoiForm';
import SertisseurMovements from '../components/SertisseurMovements';
import SertisseurRetourModule from '../components/SertisseurRetourModule';
import { ajouterSertisseur, modifierSertisseur } from '../action/AtelierAction';
import { getListeOfSerti as getListeOfSertiAction } from '../../../actions';
import { getTodaySqlFormat } from '../../../helpers/functions';

const parseNumberInput = (value) => {
    if (value === null || value === undefined) {
        return NaN;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '') {
            return 0;
        }
        const normalized = trimmed.replace(',', '.');
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : NaN;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : NaN;
};

const EnvoiSerti = ({ listeOfSerti, dispatchGetListeOfSerti }) => {
    // State - OF sélectionné
    const [selectedNSachet, setSelectedNSachet] = useState(null);
    const [selectedOfObject, setSelectedOfObject] = useState(null);
    const [currentOfRef, setCurrentOfRef] = useState(null);
    const [currentOfRefDesc, setCurrentOfRefDesc] = useState(null);
    
    // State - Mouvements et sélection
    const [historiqueData, setHistoriqueData] = useState([]);
    const [saisiesEnCours, setSaisiesEnCours] = useState([]);
    const [selectedMovement, setSelectedMovement] = useState(null);
    
    // State - UI
    const [alertMessage, setAlertMessage] = useState(null);
    const [alertSeverity, setAlertSeverity] = useState('info');
    const [loadingRetour, setLoadingRetour] = useState(false);
    
    // Refs
    const ofFormRef = useRef(null);
    const envoiFormRef = useRef(null);
    const retourFormRef = useRef(null);
    const skipRetourFocusRef = useRef(false);
    const shouldPopulateDraftRef = useRef(false);

    const scheduleFocusOnOf = useCallback(() => {
        setTimeout(() => {
            if (ofFormRef.current?.focusOfField) {
                ofFormRef.current.focusOfField();
            }
        }, 150);

        setTimeout(() => {
            skipRetourFocusRef.current = false;
        }, 300);
    }, []);

    /**
     * Appelé après fetch API du N° OF (depuis SertisseurEnvoiForm)
     * Remplit l'historique et sélectionne automatiquement un mouvement
     */
    const handleHistoriqueFetched = useCallback((data, numSachet) => {
        const skipAutoActions = skipRetourFocusRef.current;
        const shouldPopulate = shouldPopulateDraftRef.current;

        console.log('🔍 handleHistoriqueFetched - shouldPopulate:', shouldPopulate, 'skipAutoActions:', skipAutoActions);

        if (!Array.isArray(data) || data.length === 0) {
            setHistoriqueData([]);
            // NE PAS vider saisiesEnCours ici - c'est l'historique de session
            setSelectedMovement(null);
            if (!skipAutoActions) {
                setTimeout(() => envoiFormRef.current?.focusSertisseur(), 0);
            }
            return;
        }

        const historique = data.map((item, idx) => {
            const { ref, ...itemWithoutRef } = item;
            const renduSerti = Number(item.rendu_serti) || 0;
            const peseeValue = Number(item.pesee) || 0;
            return {
                id_ligne: item.id_ligne || item.id_serto || item.id || idx,
                of_serto: numSachet,
                id_serto: item.id_serto || item.id,
                label: item.nom_sertisseur || item.nom || 'Sertisseur',
                sertisseur: item.nom_sertisseur || item.nom || 'Sertisseur',
                ref: item.ref_cv ?? item.ref ?? '',
                refDesc: item.ref ?? item.refDesc ?? '',
                pds_d: item.pds_d ?? item.pds ?? 0,
                pds_r: item.pds_r ?? 0,
                prix: item.prix ?? 0,
                rendu_serti: renduSerti,
                pesee: peseeValue,
                avatar: item.avatar || null,
                ...itemWithoutRef
            };
        });

        setHistoriqueData(historique);
        const pendingMovements = historique.filter((movement) => Number(movement.rendu_serti) === 0);
        
        // Si shouldPopulate = true, AJOUTER ou REMPLACER le dernier mouvement dans SAISIES
        if (shouldPopulate && historique.length > 0) {
            const latestMovement = historique[historique.length - 1];
            console.log('➕ Ajout/Remplacement dans SAISIES:', latestMovement);
            setSaisiesEnCours((prev) => {
                // Chercher si un mouvement avec le même OF existe déjà
                const existingIndex = prev.findIndex(item => item.of_serto === latestMovement.of_serto && item.id_serto === latestMovement.id_serto);
                if (existingIndex !== -1) {
                    // Remplacer l'existant
                    const updated = [...prev];
                    updated[existingIndex] = latestMovement;
                    return updated;
                } else {
                    // Ajouter nouveau
                    return [...prev, latestMovement];
                }
            });
            shouldPopulateDraftRef.current = false;
        }

        // Stocker ref/refDesc du OF pour l'envoi
        setCurrentOfRef(data[0]?.ref_cv || null);
        setCurrentOfRefDesc(data[0]?.ref || null);

        // Si on skip les actions auto (après submit), on ne sélectionne rien et focus sur OF
        if (skipAutoActions) {
            setSelectedMovement(null);
            scheduleFocusOnOf();
            return;
        }

        // Logique normale: si pas de shouldPopulate (recherche OF)
        if (!shouldPopulate) {
            const hasPending = pendingMovements.length > 0;
            if (hasPending) {
                // Il y a des mouvements en attente -> sélectionner le premier et aller sur retour
                setSelectedMovement(pendingMovements[0]);
                setTimeout(() => {
                    if (retourFormRef.current?.focusPoidsR) {
                        retourFormRef.current.focusPoidsR();
                    }
                    const retourCard = document.querySelector('[data-retour-module]');
                    if (retourCard) {
                        retourCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 150);
            } else {
                // Pas de mouvements en attente -> aller sur envoi
                setSelectedMovement(null);
                setTimeout(() => envoiFormRef.current?.focusSertisseur(), 0);
            }
        } else {
            // Si shouldPopulate (après submit avec refresh), sélectionner le premier pending
            const pending = pendingMovements[0] || null;
            setSelectedMovement(pending);
            if (pending) {
                setTimeout(() => {
                    if (retourFormRef.current?.focusPoidsR) {
                        retourFormRef.current.focusPoidsR();
                    }
                    const retourCard = document.querySelector('[data-retour-module]');
                    if (retourCard) {
                        retourCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 150);
            } else {
                setTimeout(() => envoiFormRef.current?.focusSertisseur(), 0);
            }
        }
    }, [scheduleFocusOnOf]);

    /**
     * Appelé quand l'utilisateur change le N° OF
     * Recharge l'historique API et réinitialise
     */
    const handleOfChange = useCallback((numSachet, sertisseurID, ofObject, options = {}) => {
        setSelectedNSachet(ofObject?.num_sachet || numSachet);
        setSelectedOfObject(ofObject || null);
        setCurrentOfRef(ofObject?.ref || null);
        setCurrentOfRefDesc(ofObject?.refDesc || null);

        if (options.reset || !ofObject) {
            setSelectedMovement(null);
            // NE PAS vider saisiesEnCours (SAISIES) - c'est l'historique de session
            setHistoriqueData([]);
            shouldPopulateDraftRef.current = false;
        }
    }, []);

    // Au montage: charger les données
    useEffect(() => {
        dispatchGetListeOfSerti();
    }, [dispatchGetListeOfSerti]);

    // Effect removed - focus logic now handled in handleHistoriqueFetched

    const refreshHistorique = useCallback(
        async (contextOverride = {}) => {
            if (!ofFormRef.current?.refetchHistorique) {
                return;
            }

            try {
                await ofFormRef.current.refetchHistorique(contextOverride.num_sachet);
                // Note: refetchHistorique appelle handleHistoriqueFetched en interne
                // Pas besoin d'appeler handleHistoriqueFetched ici, ça causerait un double appel
            } catch (error) {
                console.error("Erreur lors du rafraîchissement de l'historique serti:", error);
                setAlertMessage('Impossible de rafraîchir l\'historique');
                setAlertSeverity('error');
            }
        },
        []
    );

    /**
     * Enregistrer un nouvel envoi sertisseur
     */
    const handleEnvoiAjouter = async (formData) => {
        try {
            setAlertMessage(null);
            const { sertisseur, poids } = formData;
            const currentNumSachet = selectedOfObject?.num_sachet;
            const poidsValue = parseNumberInput(poids);

            if (!selectedOfObject || !currentNumSachet) {
                setAlertMessage('Sélectionnez un N° OF');
                setAlertSeverity('warning');
                return;
            }

            if (!sertisseur?.id) {
                setAlertMessage('Sélectionnez un sertisseur');
                setAlertSeverity('warning');
                return;
            }

            if (!Number.isFinite(poidsValue) || poidsValue <= 0) {
                setAlertMessage('Poids invalide');
                setAlertSeverity('warning');
                return;
            }

            // Vérifier que la combo n'existe pas déjà
            const exists = listeOfSerti.find(
                item => String(item.id_serto) === String(sertisseur.id) &&
                        String(item.of_serto) === String(selectedOfObject.num_sachet) &&
                        (item.rendu_serti === 0 || item.rendu_serti === '0')
            );

            if (exists) {
                setAlertMessage('Combinaison déjà en cours');
                setAlertSeverity('warning');
                return;
            }

            // Créer et ajouter
            const newData = {
                id_ligne: `temp_${Date.now()}_${Math.random()}`,
                id_serto: sertisseur.id,
                id_sertisseur: sertisseur.id,
                label: sertisseur.label,
                sertisseur: sertisseur.label,
                of_serto: currentNumSachet,
                of: currentNumSachet,
                poids_d: poidsValue,
                poids: poidsValue,
                poids_r: 0,
                temps: 0,
                rendu_serti: 0,
                pesee: 0,
                avatar: sertisseur.avatar || null,
                ref: currentOfRef,
                refDesc: currentOfRefDesc,
            };

            await ajouterSertisseur(newData);

            setAlertMessage('Mouvement enregistré');
            setAlertSeverity('success');

            skipRetourFocusRef.current = true;
            shouldPopulateDraftRef.current = true;
            console.log('✅ Before refreshHistorique - shouldPopulate:', shouldPopulateDraftRef.current);
            await refreshHistorique({ num_sachet: currentNumSachet });

            setTimeout(() => setAlertMessage(null), 2000);
        } catch (error) {
            console.error("Erreur envoi:", error);
            setAlertMessage('Erreur enregistrement');
            setAlertSeverity('error');
            skipRetourFocusRef.current = false;
        }
    };

    /**
     * Enregistrer le retour sertisseur
     */
    const handleRetourAjouter = async (formData) => {
        if (!selectedMovement) {
            setAlertMessage('Sélectionnez un mouvement');
            setAlertSeverity('warning');
            return;
        }

        setAlertMessage(null);
        const { poidsR, prix } = formData;
        const poidsDepart = parseNumberInput(selectedMovement.pds_d ?? selectedMovement.poids ?? 0);
        const poidsRetour = parseNumberInput(poidsR);
        const prixValue = parseNumberInput(prix);

        if (!Number.isFinite(poidsDepart) || poidsDepart <= 0) {
            setAlertMessage('Poids départ invalide');
            setAlertSeverity('warning');
            return;
        }

        if (!Number.isFinite(poidsRetour) || poidsRetour < 0) {
            setAlertMessage('Poids retour invalide');
            setAlertSeverity('warning');
            return;
        }

        if (!Number.isFinite(prixValue) || prixValue < 0) {
            setAlertMessage('Prix invalide');
            setAlertSeverity('warning');
            return;
        }

        const idSert = selectedMovement.id_sert || selectedMovement.id;

        if (!idSert) {
            setAlertMessage('Identifiant du mouvement manquant');
            setAlertSeverity('error');
            return;
        }

        const dataToSave = {
            id_sert: idSert,
            of: selectedMovement.of_serto,
            poids: poidsDepart,
            poidsR: poidsRetour,
            prix: prixValue,
            rendu_serti: 1,
            date_retour: getTodaySqlFormat(),
        };

        try {
            setLoadingRetour(true);
            await modifierSertisseur(dataToSave);

            setAlertMessage('Retour enregistré');
            setAlertSeverity('success');

            skipRetourFocusRef.current = true;
            shouldPopulateDraftRef.current = true;
            await refreshHistorique({ num_sachet: selectedMovement.of_serto || selectedMovement.of });

            setTimeout(() => setAlertMessage(null), 2000);
        } catch (error) {
            console.error("Erreur retour:", error);
            setAlertMessage('Erreur enregistrement');
            setAlertSeverity('error');
            skipRetourFocusRef.current = false;
        } finally {
            setLoadingRetour(false);
        }
    };

    /**
     * Supprimer une ligne de l'historique
     */
    const handleDeleteMovement = (id_ligne) => {
        // Supprimer de l'historique (pas de la BD, juste local)
        setHistoriqueData(prev => prev.filter(item => item.id_ligne !== id_ligne));
        setSelectedMovement(null);
    };

    const handleDeleteFromDraft = (id_ligne) => {
        // Supprimer de SAISIES (historique session utilisateur)
        setSaisiesEnCours(prev => prev.filter(item => item.id_ligne !== id_ligne));
    };

    /**
     * Sélectionner une ligne d'historique
     */
    const handleSelectMovement = (rowData) => {
        setSelectedMovement(rowData);
    };

    /**
     * Rafraîchir l'historique et les données
     */
    const handleRefreshHistorique = useCallback(async (options = {}) => {
        const targetNumSachet = options.num_sachet || selectedNSachet || selectedOfObject?.num_sachet || null;

        skipRetourFocusRef.current = true;
        setSelectedMovement(null);

        if (!targetNumSachet) {
            scheduleFocusOnOf();
            return;
        }

        try {
            shouldPopulateDraftRef.current = true;
            await refreshHistorique({ num_sachet: targetNumSachet });
            dispatchGetListeOfSerti();
        } finally {
            scheduleFocusOnOf();
        }
    }, [dispatchGetListeOfSerti, refreshHistorique, scheduleFocusOnOf, selectedNSachet, selectedOfObject]);

    return (
        <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
            <Grid container spacing={2}>
                {/* Colonne gauche: N° OF */}
                <Grid item xs={12} md={3}>
                    <Card elevation={3} style={{ borderRadius: '8px', marginBottom: '16px' }}>
                        <CardContent style={{ padding: '12px' }}>
                            <SertisseurEnvoiForm
                                ref={ofFormRef}
                                onSubmitForm={handleEnvoiAjouter}
                                onOfChange={handleOfChange}
                                onHistoriqueFetched={handleHistoriqueFetched}
                                isOfOnly={true}
                            />
                        </CardContent>
                    </Card>

                    {/* Saisies en cours */}
                    <Card elevation={3} style={{ borderRadius: '8px' }}>
                        <CardHeader
                            title="SAISIES"
                            titleTypographyProps={{ variant: 'h6', style: { fontWeight: 500 } }}
                            action={
                                saisiesEnCours.length > 0 && (
                                    <Button
                                        variant="text"
                                        color="secondary"
                                        size="small"
                                        startIcon={<ClearIcon />}
                                        onClick={() => setSaisiesEnCours([])}
                                        style={{ fontSize: '0.75rem' }}
                                    >
                                        Vider
                                    </Button>
                                )
                            }
                            style={{
                                backgroundColor: '#fff3e0',
                                padding: '12px 16px',
                                borderBottom: '1px solid #e0e0e0'
                            }}
                        />
                        <CardContent style={{ padding: '12px', maxHeight: '300px', overflowY: 'auto' }}>
                            <SertisseurMovements
                                data={[...saisiesEnCours].reverse()}
                                onRowClick={handleSelectMovement}
                                onDelete={handleDeleteFromDraft}
                                minimal={true}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                {/* Colonne droite: Envoi + Retour + Historique */}
                <Grid item xs={12} md={9}>
                    <Grid container spacing={2}>
                        {/* Envoi */}
                        <Grid item xs={12} md={6}>
                            <Card elevation={3} style={{ borderRadius: '8px', height: '280px', display: 'flex', flexDirection: 'column' }}>
                                <CardHeader
                                    title="ENVOI"
                                    titleTypographyProps={{ variant: 'h6', style: { fontWeight: 500 } }}
                                    style={{
                                        backgroundColor: '#e3f2fd',
                                        padding: '12px 20px',
                                        borderBottom: '1px solid #e0e0e0'
                                    }}
                                />
                                <CardContent style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    {!selectedOfObject ? (
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                            backgroundColor: '#fafafa',
                                            borderRadius: '8px',
                                            border: '1px dashed #e0e0e0',
                                            padding: '32px',
                                            textAlign: 'center'
                                        }}>
                                            <InfoOutlinedIcon style={{ fontSize: 48, color: '#bdbdbd', marginBottom: '16px' }} />
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>
                                                Renseigner N° OF
                                            </span>
                                        </div>
                                    ) : selectedMovement && (selectedMovement.rendu_serti === 0 || selectedMovement.rendu_serti === '0') ? (
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                            backgroundColor: '#fff3e0',
                                            borderRadius: '8px',
                                            border: '1px solid #ffe0b2',
                                            padding: '32px',
                                            textAlign: 'center'
                                        }}>
                                            <InfoOutlinedIcon style={{ fontSize: 48, color: '#ff9800', marginBottom: '16px' }} />
                                            <span style={{ color: '#e65100', fontSize: '0.9rem', fontWeight: 500 }}>
                                                Mouvement en cours de retour
                                            </span>
                                        </div>
                                    ) : (
                                        <SertisseurEnvoiForm
                                            ref={envoiFormRef}
                                            onSubmitForm={handleEnvoiAjouter}
                                            selectedOf={selectedOfObject}
                                            isFormOnly={true}
                                            hideSubmitButton={true}
                                        />
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Retour */}
                        <Grid item xs={12} md={6}>
                            <Card elevation={3} style={{ borderRadius: '8px', height: '280px', display: 'flex', flexDirection: 'column' }} data-retour-module>
                                <CardHeader
                                    title="RETOUR"
                                    titleTypographyProps={{ variant: 'h6', style: { fontWeight: 500 } }}
                                    style={{
                                        backgroundColor: '#f3e5f5',
                                        padding: '12px 20px',
                                        borderBottom: '1px solid #e0e0e0'
                                    }}
                                />
                                <CardContent style={{ padding: 0, flex: 1 }}>
                                    <SertisseurRetourModule
                                        ref={retourFormRef}
                                        selectedItem={selectedMovement}
                                        onSubmit={handleRetourAjouter}
                                        loading={loadingRetour}
                                        hideSubmitButton={true}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Historique */}
                        <Grid item xs={12}>
                            <Card elevation={3} style={{ borderRadius: '8px' }}>
                                <CardHeader
                                    title="HISTORIQUE"
                                    titleTypographyProps={{ variant: 'h6', style: { fontWeight: 500 } }}
                                    style={{
                                        backgroundColor: '#e3f2fd',
                                        padding: '12px 20px',
                                        borderBottom: '1px solid #e0e0e0'
                                    }}
                                />
                                <CardContent style={{ padding: '12px', width: '100%' }}>
                                    {historiqueData.length === 0 ? (
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#fafafa',
                                            borderRadius: '8px',
                                            border: '1px dashed #e0e0e0',
                                            padding: '32px',
                                            textAlign: 'center',
                                            minHeight: '200px'
                                        }}>
                                            <InfoOutlinedIcon style={{ fontSize: 48, color: '#bdbdbd', marginBottom: '16px' }} />
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>
                                                Renseigner N° OF
                                            </span>
                                        </div>
                                    ) : (
                                        <SertisseurMovements
                                            data={historiqueData}
                                            onRowClick={handleSelectMovement}
                                            onDelete={handleDeleteMovement}
                                            onRefresh={handleRefreshHistorique}
                                            selectedMovement={selectedMovement}
                                            useListView={true}
                                        />
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Snackbar
                open={!!alertMessage}
                autoHideDuration={3000}
                onClose={() => setAlertMessage(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setAlertMessage(null)}
                    severity={alertSeverity}
                    variant="filled"
                >
                    {alertMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

const mapStateToProps = (state) => ({
    listeOfSerti: state.generalData.listeOfSerti || [],
});

const mapDispatchToProps = (dispatch) => ({
    dispatchGetListeOfSerti: () => dispatch(getListeOfSertiAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EnvoiSerti);
